import { createApi } from '@reduxjs/toolkit/query/react';
import { Address } from 'viem';

import { ProjectStageEnum } from 'src/global/enums/project.enum';
import type {
  DepositStatusResponseType,
  DepositStatusType,
  ProjectAdminWalletResponseType,
  ProjectAdminWalletType,
  ProjectDetailResponseType,
  ProjectDetailType,
  ProjectListResponseType,
  ProjectUpdatePayloadType,
  ValidateDomainResponseType,
  ValidateDomainType,
  VerifyDepositRequestType,
  VerifyDepositResponseType,
} from 'src/global/schemas/project.schema';

import { REDUX_SLICE } from '../constants';
import { axiosBaseQuery } from '../utils';

export const projectApi = createApi({
  reducerPath: REDUX_SLICE.PROJECT_API,
  tagTypes: ['Project', 'AdminWallet'],
  baseQuery: axiosBaseQuery,
  endpoints: builder => ({
    createProject: builder.mutation<any, { creator: string }>({
      query: data => ({
        method: 'POST',
        path: '/v1/programs',
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': data.creator,
        },
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data;
      },
    }),
    getProjectList: builder.query<
      ProjectListResponseType,
      { page: number; limit: number; state?: string; address: string }
    >({
      query: ({ page, limit, state, address }) => ({
        method: 'GET',
        path: '/v1/programs',
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
        params: {
          page,
          size: limit,
          filter: JSON.stringify({
            ...(state && { state }),
          }),
        },
      }),
      transformResponse: (returnValue: ProjectListResponseType) => {
        return returnValue;
      },
      providesTags: (_result, _error, { state }) => [{ type: 'Project', id: state }],
    }),
    getAdminWallet: builder.query<ProjectAdminWalletType, { address: string }>({
      query: ({ address }) => ({
        method: 'GET',
        path: '/v1/admin-wallets',
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: ProjectAdminWalletResponseType) => {
        return returnValue.data;
      },
      providesTags: (_result, _error, { address }) => [{ type: 'AdminWallet', id: address }],
    }),
    getProjectDetail: builder.query<ProjectDetailType, { id: string | undefined; address: Address | undefined }>({
      query: ({ id, address }) => ({
        method: 'GET',
        path: `/v1/programs/${id}`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: ProjectDetailResponseType) => {
        return returnValue.data;
      },
      providesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
    }),
    updateProject: builder.mutation<ProjectDetailType, ProjectUpdatePayloadType>({
      query: ({ id, data, preCheck }) => ({
        method: 'PUT',
        path: `v1/programs/${id}`,
        data: {
          ...data,
          ...(preCheck && { preCheck }),
        },
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': data.creator,
        },
        shouldSign: true,
      }),
      transformResponse: (returnValue: ProjectDetailResponseType) => {
        return returnValue.data ?? returnValue;
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Project', id }],
    }),
    validateDomain: builder.mutation<ValidateDomainType, { dns: string; address?: string }>({
      query: ({ address, ...data }) => ({
        method: 'POST',
        path: '/v1/verify-dns',
        data,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: ValidateDomainResponseType) => {
        return returnValue.data;
      },
    }),
    registerDomain: builder.mutation<ValidateDomainType, { dns: string; address?: string }>({
      query: ({ address, ...data }) => ({
        method: 'POST',
        path: '/v1/register-dns',
        data,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: ValidateDomainResponseType) => {
        return returnValue.data;
      },
    }),
    uploadNFTAsset: builder.mutation<{ data: { name: string } }, { programId: string; file: File; address: string }>({
      query: ({ programId, file, address }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          method: 'POST',
          path: `/v1/programs/${programId}/nfts`,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-nodesale-lang': 'en',
            'x-offchain-address': address,
          },
          shouldSign: true,
        };
      },
      transformResponse: (returnValue: { data: { name: string } }) => {
        return returnValue;
      },
    }),
    getDepositStatus: builder.query<DepositStatusType, { programId: string; address: string }>({
      query: ({ programId, address }) => ({
        method: 'GET',
        path: `/v1/programs/${programId}/deposit`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: DepositStatusResponseType) => {
        return returnValue.data;
      },
    }),
    verifyDeposit: builder.mutation<
      VerifyDepositResponseType,
      { programId: string; address: string } & VerifyDepositRequestType
    >({
      query: ({ programId, txId, address }) => ({
        method: 'POST',
        path: `/v1/programs/${programId}/deposit`,
        data: { txId },
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
        shouldSign: true,
      }),
      transformResponse: (returnValue: { data: VerifyDepositResponseType }) => {
        return returnValue?.data ?? returnValue;
      },
      invalidatesTags: (_result, _error, { programId }) => [{ type: 'Project', id: programId }],
    }),
    requestVoting: builder.mutation<any, { programId: string; address: string }>({
      query: data => ({
        method: 'POST',
        path: `/v1/programs/${data.programId}/request`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': data.address,
        },
      }),
      transformResponse: (returnValue: any) => {
        return returnValue?.data ?? returnValue;
      },
      invalidatesTags(_result, _error, arg, _meta) {
        return [
          { type: 'Project', id: arg.programId },
          { type: 'Project', id: ProjectStageEnum.STAGING },
        ];
      },
    }),
    cancelVoting: builder.mutation<any, { programId: string; address: string }>({
      query: data => ({
        method: 'POST',
        path: `/v1/programs/${data.programId}/cancel`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': data.address,
        },
        shouldSign: true,
      }),
      invalidatesTags(_result, _error, arg, _meta) {
        return [
          { type: 'Project', id: arg.programId },
          { type: 'Project', id: ProjectStageEnum.STAGING },
        ];
      },
    }),
    publishProjectToStaging: builder.mutation<any, { programId: string; address: string }>({
      query: data => ({
        method: 'POST',
        path: `/v1/programs/${data.programId}/publish`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': data.address,
        },
        shouldSign: true,
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data;
      },
    }),
    publishProjectToProd: builder.mutation<any, { programId: string; sale: string; address: string }>({
      query: data => ({
        method: 'POST',
        path: `/v1/programs/${data.programId}/release`,
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': data.address,
        },
        data: {
          sale: data.sale,
        },
        shouldSign: true,
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data;
      },
      invalidatesTags(_result, _error, arg, _meta) {
        return [
          { type: 'Project', id: arg.programId },
          { type: 'Project', id: ProjectStageEnum.STAGING },
        ];
      },
    }),
    getRecipients: builder.query<
      Array<{
        networkId: string;
        address: string;
        percent: number;
        name: string;
        createdAt: string;
        updatedAt: string;
      }>,
      { address: string }
    >({
      query: ({ address }) => ({
        method: 'GET',
        path: '/v1/recipients',
        headers: {
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
        },
      }),
      transformResponse: (returnValue: any) => {
        return returnValue.data || [];
      },
    }),
  }),
});

export const {
  useGetAdminWalletQuery,
  useCreateProjectMutation,
  useGetProjectListQuery,
  useGetProjectDetailQuery,
  useUpdateProjectMutation,
  usePublishProjectToStagingMutation,
  usePublishProjectToProdMutation,
  useRequestVotingMutation,
  useCancelVotingMutation,
  useValidateDomainMutation,
  useRegisterDomainMutation,
  useUploadNFTAssetMutation,
  useGetDepositStatusQuery,
  useLazyGetDepositStatusQuery,
  useVerifyDepositMutation,
  useGetRecipientsQuery,
} = projectApi;
